/**
 * Prospects page - List view with CSV upload and persistent dialer
 */

import { useState } from 'react';
import { Upload, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { exportProspectsCSV } from '@/lib/api/prospects';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { CSVUpload } from '@/components/prospects/CSVUpload';
import { ProspectList } from '@/components/prospects/ProspectList';
import { CallUI } from '@/components/CallUI';
import { useHasDialer, useCanWrite } from '@/lib/auth/AuthProvider';

export function ProspectsPage() {
  const hasDialer = useHasDialer();
  const canWrite = useCanWrite();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState<{
    id: string;
    phone: string;
    name?: string;
  } | null>(null);

  const handleCallProspect = (prospectId: string, phone: string, name?: string) => {
    setSelectedProspect({ id: prospectId, phone, name });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportProspectsCSV();
    } catch (e) {
      console.error('Export failed:', e);
      alert(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="h-full flex">
      {/* Left side - Prospects list (70%) */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Cold Dialing</h1>
            <p className="text-sm text-muted-foreground">
              Outbound calls to imported prospects
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            {canWrite && (
              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload CSV
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Import Prospects from CSV</DialogTitle>
                    <DialogDescription>
                      Upload one or more CSV files containing prospect information
                    </DialogDescription>
                  </DialogHeader>
                  <CSVUpload />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <ProspectList onCallProspect={handleCallProspect} />
        </div>
      </div>

      {/* Right side - Persistent dialer panel (hidden for CRM-only users) */}
      {hasDialer && (
        <div className="w-96 border-l bg-muted/30 flex flex-col">
          <div className="border-b px-6 py-4">
            <h2 className="text-lg font-semibold">Dialer</h2>
            <p className="text-xs text-muted-foreground">
              {selectedProspect
                ? `Calling ${selectedProspect.name || 'prospect'}`
                : 'Select a prospect to call'}
            </p>
          </div>

          <div className="flex-1 overflow-auto p-6">
            <Card className="p-4">
              <CallUI
                prefilledPhone={selectedProspect?.phone}
                prospectId={selectedProspect?.id}
                prospectName={selectedProspect?.name}
              />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
